import { useEffect, useState } from "react";
import axios from "axios";
import Dropdown from "@/components/Dropdown/index";
import PropTypes from 'prop-types';
import { apiProvider } from "@/services/api/utils/provider";

const buildOptionLabel = (item, optionKey, optionValue) => {
    if (Array.isArray(optionValue)) {
        return optionValue.map(v => item[v]).filter(Boolean).join(' - ');
    }
    const valueLabel = item[optionValue] || '';
    return valueLabel ? `${item[optionKey]} (${valueLabel})` : item[optionKey];
};

const CellDynamicSelectComponent = ({
    isReadOnly,
    schema,
    payload,
    parentProps,
    inputProps,
    setInputProps,
    validateSchema,
    getFieldValue,
    updatePayloadWithSiblings,
}) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    const uiOptions = schema["x-uioptions"];
    const optionKey = uiOptions?.key;
    const optionValue = uiOptions?.value;
    const fieldsToPopulate = uiOptions?.["fields-to-populate"];

    useEffect(() => {
        if (!uiOptions?.url) return;
        setLoading(true);

        const mapItems = (items) => items.map((item, index) => ({
            value: item[optionKey],
            label: buildOptionLabel(item, optionKey, optionValue),
            index,
            original: item,
        }));

        const url = `${apiProvider.BASE_URL}/${uiOptions.url}`;
        axios.get(url)
            .then(res => {
                const data = res.data;
                const records = data?.records || data?.data || data || [];
                const items = Array.isArray(records) ? records : [];
                setOptions(mapItems(items));
            })
            .catch(() => setOptions([]))
            .finally(() => setLoading(false));
    }, [uiOptions?.url]);

    useEffect(() => {
        setInputProps({
            ...inputProps,
            disabled: isReadOnly,
            value: getFieldValue(payload, parentProps),
        });
    }, [payload]);

    function onSelect(selectedValue, originalItem) {
        if (isReadOnly) return;

        setInputProps({ ...inputProps, value: selectedValue });

        const siblings = {};
        if (fieldsToPopulate && originalItem) {
            for (const [targetPath, sourceField] of Object.entries(fieldsToPopulate)) {
                siblings[targetPath] = originalItem[sourceField] ?? '';
            }
        }

        updatePayloadWithSiblings(selectedValue, siblings);
        validateSchema(selectedValue);
    }

    const storedValue = inputProps?.value;
    const displayValue = isReadOnly ? storedValue : options.find(o => o.value === storedValue)?.label;

    let placeholder;
    if (loading) {
        placeholder = "Loading...";
    } else if (!isReadOnly) {
        placeholder = "Select Option";
    }

    return (
        <Dropdown
            placeholder={placeholder}
            value={displayValue}
            disabled={isReadOnly || loading}
        >
            {options.map(({ label, value, index, original }) => (
                <Dropdown.Item
                    key={index}
                    id={label}
                    onClick={() => onSelect(value, original)}
                    value
                >
                    {label}
                </Dropdown.Item>
            ))}
        </Dropdown>
    );
};

CellDynamicSelectComponent.propTypes = {
    isReadOnly: PropTypes.any,
    schema: PropTypes.any,
    payload: PropTypes.any,
    parentProps: PropTypes.any,
    inputProps: PropTypes.any,
    setInputProps: PropTypes.any,
    validateSchema: PropTypes.any,
    getFieldValue: PropTypes.any,
    updatePayloadWithSiblings: PropTypes.func,
};

export default CellDynamicSelectComponent;
